/**
 * Aantallen die bij de werkelijk mogelijke drukmethode horen.
 *
 * De klant kiest geen drukmethode; de optie-API voegt de aantallenladders van
 * alle methodes samen (digitaal 1+, offset 250+). Maar de catalogus-excludes
 * kunnen een methode voor een samenstelling uitsluiten. Sleeves met formaat
 * `sleeve_shipping_box_33` en materiaal 300gr_hvo mogen bijvoorbeeld niet
 * op `digital_large` en niet op `digital`, dus alleen offset, en die begint pas
 * bij 250 stuks. De configurator bood toch 1, 10, 25, 50 en 100 aan; de
 * leverancier weigerde die ("copies does not match any range set") en de klant
 * zag "niet leverbaar".
 *
 * Deze module bepaalt per samenstelling welke methodes overblijven na de
 * excludes en beperkt de aantallen tot de ladders van die methodes. Blijven
 * alle methodes over (of geen enkele), dan geeft hij `null`: dan verandert er
 * niets ten opzichte van het oude gedrag.
 *
 * Bewust alleen op basis van excludes, niet op een vastgezette methode
 * (autoSetOptions): de prijsroute kan bij een aantal buiten de ladder zelf een
 * andere methode kiezen, en die afweging hoort hier niet te worden overgedaan.
 */

export type QuantityRange = { min: number; max: number; step: number };
export type QuantityRangesByPrintingMethod = Record<string, { ranges: QuantityRange[]; summary: number[] }>;

type ExcludeCondition = { property?: string; options?: Array<string | number> };
type ExcludeRules = Array<Array<ExcludeCondition>>;

const toNumber = (value: unknown) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : NaN;
};

/** Uit de opgeslagen Print.com-snapshot: per drukmethode de bereiken van `copies`. */
export function buildQuantityRangesByPrintingMethod(connectedProduct: unknown): QuantityRangesByPrintingMethod | null {
  const properties = (connectedProduct as any)?.properties;
  if (!Array.isArray(properties)) return null;
  const copies = properties.find((property: any) => property?.slug === 'copies');
  const rangeSets = Array.isArray(copies?.rangeSets) ? copies.rangeSets : [];
  const result: QuantityRangesByPrintingMethod = {};
  for (const rangeSet of rangeSets) {
    const method = String(rangeSet?.printingmethod || '');
    // Een sample is geen productieoplage.
    if (!method || method === 'sample') continue;
    const ranges = (Array.isArray(rangeSet?.options) ? rangeSet.options : [])
      .map((range: any) => ({
        min: toNumber(range?.min),
        max: toNumber(range?.max),
        step: Math.max(1, toNumber(range?.steps ?? range?.step) || 1),
      }))
      .filter((range: QuantityRange) => range.min > 0 && range.max >= range.min);
    if (!ranges.length) continue;
    const summary = (Array.isArray(rangeSet?.summary) ? rangeSet.summary : [])
      .map(toNumber)
      .filter((value: number) => Number.isSafeInteger(value) && value > 0);
    const existing = result[method];
    result[method] = existing
      ? { ranges: [...existing.ranges, ...ranges], summary: [...existing.summary, ...summary] }
      : { ranges, summary };
  }
  return Object.keys(result).length >= 2 ? result : null;
}

function isExcluded(configuration: Record<string, unknown>, excludes: ExcludeRules) {
  return excludes.some((rule) => Array.isArray(rule) && rule.length > 0 && rule.every((condition) => {
    const property = String(condition?.property || '');
    const values = Array.isArray(condition?.options) ? condition.options.map(String) : [];
    if (!property || !values.length) return false;
    const selected = configuration[property];
    return selected !== undefined && selected !== null && selected !== '' && values.includes(String(selected));
  }));
}

export function quantityFitsRanges(quantity: number, ranges: QuantityRange[]) {
  return ranges.some((range) => quantity >= range.min && quantity <= range.max && (quantity - range.min) % range.step === 0);
}

/** Drukmethodes die voor deze samenstelling niet door de excludes worden uitgesloten. */
export function allowedPrintingMethods(
  rangesByMethod: QuantityRangesByPrintingMethod,
  excludes: unknown,
  configuration: Record<string, unknown>,
): string[] {
  const rules = Array.isArray(excludes) ? excludes as ExcludeRules : [];
  return Object.keys(rangesByMethod).filter((method) => !isExcluded({ ...configuration, printingmethod: method }, rules));
}

export type EffectiveQuantityPolicy = {
  type: 'minimum';
  minimum: number;
  maximum: number;
  ranges: QuantityRange[];
  values: number[];
  allowedPrintingMethods: string[];
};

export function resolvePrintingMethodQuantityPolicy(args: {
  rangesByMethod: QuantityRangesByPrintingMethod | null | undefined;
  excludes: unknown;
  configuration: Record<string, unknown>;
  /** De aantallen die de winkel nu aanbiedt; die blijven over als ze passen. */
  candidateQuantities?: unknown[];
}): EffectiveQuantityPolicy | null {
  const rangesByMethod = args.rangesByMethod;
  if (!rangesByMethod || typeof rangesByMethod !== 'object') return null;
  const methods = Object.keys(rangesByMethod);
  if (methods.length < 2) return null;
  const allowed = allowedPrintingMethods(rangesByMethod, args.excludes, args.configuration);
  if (allowed.length === 0 || allowed.length === methods.length) return null;

  // Fijnste stap eerst: normalizeQuantityForPolicy neemt het eerste passende bereik.
  const ranges = allowed
    .flatMap((method) => rangesByMethod[method].ranges)
    .sort((a, b) => a.step - b.step || a.min - b.min);
  const minimum = Math.min(...ranges.map((range) => range.min));
  const maximum = Math.max(...ranges.map((range) => range.max));
  const unique = (values: number[]) => [...new Set(values)].sort((a, b) => a - b);
  const candidates = unique((args.candidateQuantities || [])
    .map(toNumber)
    .filter((value) => Number.isSafeInteger(value) && value > 0 && quantityFitsRanges(value, ranges)));
  const values = candidates.length
    ? candidates
    : unique(allowed.flatMap((method) => rangesByMethod[method].summary).filter((value) => quantityFitsRanges(value, ranges)));
  return { type: 'minimum', minimum, maximum, ranges, values: values.length ? values : [minimum], allowedPrintingMethods: allowed };
}
